import {
  Box,
  Button as ChakraButton,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text as ChakraText,
} from '@chakra-ui/react';
import { SingleDatePicker } from '@diamond/shared/ui';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { type UseFormReturn, FormProvider } from 'react-hook-form';

const SUBHEADER_TEXT_STYLE = {
  fontWeight: 400,
  fontSize: '10px',
  lineHeight: '100%',
  letterSpacing: '0%',
  color: '#8E8E93',
} as const;

interface MutationSearchFiltersProps {
  searchValue: string;
  memoLineSearchValue: string;
  mutationAmount: string;
  searchError: string;
  isLoading: boolean;
  methods: UseFormReturn<{ mutationDate: string }>;
  onSearchValueChange: (value: string) => void;
  onMemoLineSearchValueChange: (value: string) => void;
  onMutationAmountChange: (value: string) => void;
  onSearch: () => void;
  onClearSearch: () => void;
  onClearMemoLineSearch: () => void;
}

export function MutationSearchFilters({
  searchValue,
  memoLineSearchValue,
  mutationAmount,
  searchError,
  isLoading,
  methods,
  onSearchValueChange,
  onMemoLineSearchValueChange,
  onMutationAmountChange,
  onSearch,
  onClearSearch,
  onClearMemoLineSearch,
}: MutationSearchFiltersProps) {
  return (
    <FormControl mb={8}>
      <Box
        display="flex"
        flexWrap="wrap"
        gap={3}
        alignItems="end"
        justifyContent="flex-start"
      >
        <Box maxW="300px" w="100%">
          <FormLabel {...SUBHEADER_TEXT_STYLE} mb={2}>
            Nomor Mutasi
          </FormLabel>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Icon as={SearchIcon} boxSize={4} color="#A0AEC0" />
            </InputLeftElement>
            <Input
              aria-label="Nomor Mutasi"
              value={searchValue}
              placeholder="Cari no.mutasi"
              onChange={(event) => onSearchValueChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  onSearch();
                }
              }}
              borderColor="#C7C7CC"
            />
            {searchValue ? (
              <InputRightElement>
                <IconButton
                  aria-label="Bersihkan pencarian"
                  icon={<Icon as={CloseIcon} boxSize={3.5} />}
                  size="xs"
                  variant="ghost"
                  onClick={onClearSearch}
                />
              </InputRightElement>
            ) : null}
          </InputGroup>
        </Box>
        <Box maxW="300px" w="100%">
          <FormLabel {...SUBHEADER_TEXT_STYLE} mb={2}>
            Cari Memo Line
          </FormLabel>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Icon as={SearchIcon} boxSize={4} color="#A0AEC0" />
            </InputLeftElement>
            <Input
              aria-label="Cari Memo Line"
              value={memoLineSearchValue}
              placeholder="Cari memo line"
              onChange={(event) =>
                onMemoLineSearchValueChange(event.target.value)
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  onSearch();
                }
              }}
              borderColor="#C7C7CC"
            />
            {memoLineSearchValue ? (
              <InputRightElement>
                <IconButton
                  aria-label="Bersihkan pencarian memo line"
                  icon={<Icon as={CloseIcon} boxSize={3.5} />}
                  size="xs"
                  variant="ghost"
                  onClick={onClearMemoLineSearch}
                />
              </InputRightElement>
            ) : null}
          </InputGroup>
        </Box>
        <Box maxW="190px" w="100%">
          <FormLabel {...SUBHEADER_TEXT_STYLE} mb={2}>
            Tanggal Mutasi
          </FormLabel>
          <FormProvider {...methods}>
            <SingleDatePicker
              name="mutationDate"
              mode="single"
              format="dd/MM/yy"
              isClearable
              clearAriaLabel="Bersihkan tanggal mutasi"
            />
          </FormProvider>
        </Box>
        <Box maxW="160px" w="100%">
          <FormLabel {...SUBHEADER_TEXT_STYLE} mb={2}>
            Nilai Mutasi (Rp)
          </FormLabel>
          <Input
            value={mutationAmount}
            placeholder="input nominal"
            onChange={(event) => onMutationAmountChange(event.target.value)}
            borderColor="#C7C7CC"
          />
        </Box>
        <ChakraButton
          variant="outline"
          borderColor="#0000A3"
          color="#0000A3"
          bgColor="white"
          _hover={{ bgColor: '#F6F7FF' }}
          minW="68px"
          h="40px"
          px={6}
          fontWeight={500}
          fontSize="16px"
          onClick={onSearch}
          isLoading={isLoading}
        >
          Cari
        </ChakraButton>
      </Box>
      {searchError ? (
        <ChakraText mt={2} fontSize="xs" color="red.500">
          {searchError}
        </ChakraText>
      ) : null}
    </FormControl>
  );
}
